import { useState } from "react";
import { Eye, EyeOff, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";

const HERO = "https://static.prod-images.emergentagent.com/jobs/43644409-e537-414a-bd4b-3cbcf13a3d00/images/1b6488d58e11608eeebabe4ee7f890e15c7d08e15df93bcd5040b6d9345e06d9.jpeg";

const Sigil = ({ className }) => (
  <svg viewBox="0 0 200 200" className={className} fill="none" stroke="currentColor" strokeWidth="0.6">
    <circle cx="100" cy="100" r="96" /><circle cx="100" cy="100" r="70" /><circle cx="100" cy="100" r="34" />
    {[0, 60, 120, 180, 240, 300].map((a) => <circle key={a} cx={100 + 35 * Math.cos((a * Math.PI) / 180)} cy={100 + 35 * Math.sin((a * Math.PI) / 180)} r="35" />)}
    <polygon points="100,4 183,148 17,148" /><polygon points="100,196 17,52 183,52" />
  </svg>
);

export const Logo = ({ className = "" }) => (
  <div className={`flex items-center gap-2 ${className}`} data-testid="brand-logo">
    <div className="w-8 h-8 rounded-md bg-[#FFB800] flex items-center justify-center shadow-[0_0_18px_rgba(255,184,0,0.45)]">
      <Zap className="w-4 h-4 text-[#0B0C10]" fill="#0B0C10" />
    </div>
    <span className="font-display font-semibold tracking-tight text-[15px]">Celestial Light <span className="text-[#FFB800]">Studio</span></span>
  </div>
);

export const AuthLayout = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] mystic-bg" data-testid="auth-layout">
    <div className="relative hidden lg:block overflow-hidden">
      <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07060f] via-[#07060f]/30 to-transparent" />
      <div className="absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-[#07060f] to-transparent" />
      <div className="absolute bottom-0 left-0 p-14 max-w-2xl fade-up">
        <p className="font-mystic text-xs tracking-[0.45em] text-[#FFB800] mb-5">✦ GRADE · MIX · SCORE · ANIMATE ✦</p>
        <h1 className="font-mystic text-4xl lg:text-5xl font-semibold leading-[1.15] text-[#F5EEDC] drop-shadow-[0_0_24px_rgba(168,85,247,0.45)]">Channel the light.<br />Shape the story.</h1>
        <p className="font-oracle italic mt-5 text-[#d8cfee] text-xl leading-snug max-w-md">Where every frame is touched by starlight — conjure film looks, summon sound, and compose scores born of the stories you love.</p>
      </div>
    </div>
    <div className="relative flex flex-col justify-center px-6 sm:px-14 py-12 overflow-hidden">
      <div className="stars" />
      <Sigil className="sigil absolute -right-40 -top-40 w-[560px] h-[560px] text-[#A855F7]/15 pointer-events-none" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full bg-[#A855F7]/10 blur-[110px] pointer-events-none" />
      <Logo className="mb-14 relative" />
      <div className="relative w-full max-w-sm mx-auto lg:mx-0 fade-up p-6 sm:p-8 sm:-m-8 rounded-2xl border border-[#A855F7]/20 bg-[#0d0b1c]/70 backdrop-blur-xl shadow-[0_0_60px_rgba(168,85,247,0.12)]">
        <h2 className="font-mystic text-3xl font-semibold tracking-wide text-[#F5EEDC]">{title}</h2>
        <p className="font-oracle italic text-[#b9aedb] text-lg mt-1 mb-8">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

export const PasswordInput = ({ testId, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} className="h-11 bg-[#0a0816] border-[#2d2650] pr-11 mystic-input" data-testid={testId} {...props} />
      <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${testId}-toggle`}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#FFB800] transition-colors">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

export const FieldLabel = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[11px] uppercase font-mono-ui tracking-wider text-slate-400 mb-2">{children}</label>
);

export const FormError = ({ error }) =>
  error ? <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="auth-error">{error}</p> : null;
